import isEqual from 'react-fast-compare';

import {formatGridPosition, isGrid} from 'samples/SampleUtil';
import GridCellKey, {getGridCellMoveOutKey} from 'test/lib/cypress/shared/GridCellKey';
import APIUtil from 'util/APIUtil';

import {MOVE_OUTCOMES} from './UploadContainersFlowConstants';

/**
 * Checks if a container was moved out of the grid
 *
 * A container was moved out if it no longer has a position
 */
function wasMovedOut(container) {
  return !container.gridCell;
}

/**
 * Checks if an affected grid was actually moved to a different position
 */
function didNotMove(container, {parentStorable, gridCell}) {
  return (
    parentStorable &&
    container.parentStorableId === parentStorable.id &&
    isEqual(container.gridCell, gridCell)
  );
}

/**
 * Checks if a container was moved in
 *
 * A container was moved in if its previous parent storable was null (meaning it was moved from root) or its
 * previous parent does not equal the current parent, the grid
 */
function wasMovedIn(prevParentStorable, currParentStorable) {
  return !prevParentStorable || prevParentStorable.id !== currParentStorable.id;
}

/**
 * Format the grid position for a container in a grid
 */
function formatContainerGridPosition(parentStorable, {rowIndex, columnIndex}) {
  const parentType = APIUtil.apiIdToType(parentStorable.id);
  return formatGridPosition({
    gridType: parentType,
    rowIndex,
    columnIndex,
    width: parentStorable.storableSchema.width,
    includeNoun: true,
  });
}

/**
 * Format a container's parent storable
 */
function formatContainerParentStorable(parentStorable, gridCell) {
  if (!parentStorable) {
    return 'root';
  }
  if (isGrid(parentStorable.id)) {
    return `${formatContainerGridPosition(parentStorable, gridCell)} of ${parentStorable.barcode}`;
  } else {
    return parentStorable.barcode;
  }
}

function nestedSet(object, rowIndex, columnIndex, value) {
  if (!object.hasOwnProperty(rowIndex)) {
    object[rowIndex] = {};
  }
  object[rowIndex][columnIndex] = value;
}

/**
 * Filter to just the changes involving updates to existing containers.
 * @param {array} changes Array of changes from upload containers validation
 * @returns Array of changes with create / noChange elements filtered out
 */
export function filterChangesToUpdatesOnly(changes) {
  return changes.filter(
    (change) => change.previous != null && !didNotMove(change.container, change.previous)
  );
}

/**
 * Parse the changes returned by the upload containers validation
 * @param {Grid} gridPreview The grid shape for the uncommitted preview
 * @param {array} changes Array of changes from the upload containers validation
 * @returns An object with keys
 *    gridCellProps {Object} 2D mapping of grid cell props
 *    changeStats {Object} A count of ech change type
 */
export default function parseChanges(gridPreview, changes) {
  const gridCellProps = {};
  const changeStats = {
    moveOut: 0,
    create: 0,
    moveIn: 0,
    reposition: 0,
    noChange: 0,
  };
  const orphanChanges = [];
  changes.forEach((change) => {
    const {container} = change;

    if (wasMovedOut(container)) {
      orphanChanges.push(change);
      return;
    }

    let changeKey;
    let extraTooltipText;
    if (!change.previous) {
      changeKey = 'create';
    } else if (didNotMove(container, change.previous)) {
      changeKey = 'noChange';
    } else {
      const {parentStorable: prevParentStorable, gridCell: prevGridCell} = change.previous;
      const formattedGridCell = formatContainerGridPosition(gridPreview, container.gridCell);
      if (wasMovedIn(prevParentStorable, gridPreview)) {
        changeKey = 'moveIn';
        const formattedPrevParent = formatContainerParentStorable(prevParentStorable, prevGridCell);
        extraTooltipText = `from ${formattedPrevParent} to ${formattedGridCell}`;
      } else {
        changeKey = 'reposition';
        const formattedPrevGridCell = formatContainerGridPosition(gridPreview, prevGridCell);
        extraTooltipText = `from ${formattedPrevGridCell} to ${formattedGridCell}`;
      }
    }

    const singleGridCellProps = {
      tooltipTitle: `${MOVE_OUTCOMES[changeKey].name}: ${container.barcode}`,
      innerClassName: MOVE_OUTCOMES[changeKey].gridCellClassName,
      dataTestAttrs: MOVE_OUTCOMES[changeKey].dataTestAttrs,
    };
    if (extraTooltipText) {
      singleGridCellProps.tooltipTitle += ` ${extraTooltipText}`;
    }
    changeStats[changeKey] += 1;

    const {rowIndex, columnIndex} = change.container.gridCell;
    nestedSet(gridCellProps, rowIndex, columnIndex, singleGridCellProps);
  });

  orphanChanges.forEach((change) => {
    const {rowIndex, columnIndex} = change.previous.gridCell;
    const tooltipTitle = `${MOVE_OUTCOMES.moveOut.name}: ${change.container.barcode}`;
    const innerClassName = MOVE_OUTCOMES.moveOut.gridCellClassName;
    const dataTestAttrs = {key: GridCellKey.EMPTY_MOVE_OUT};

    if (gridCellProps.hasOwnProperty(rowIndex) && gridCellProps[rowIndex].hasOwnProperty(columnIndex)) {
      const singleGridCellProps = gridCellProps[rowIndex][columnIndex];
      singleGridCellProps.tooltipTitle += `\n${tooltipTitle}`;
      singleGridCellProps.innerClassName += ` ${innerClassName}`;
      singleGridCellProps.dataTestAttrs = {
        key: getGridCellMoveOutKey(singleGridCellProps.dataTestAttrs.key),
      };
    } else {
      nestedSet(gridCellProps, rowIndex, columnIndex, {tooltipTitle, innerClassName, dataTestAttrs});
    }
  });
  changeStats.moveOut = orphanChanges.length;
  return {gridCellProps, changeStats};
}
